/**
 * Helpers for the searchable product selector used by the Reservations and
 * Dispatches item rows.
 *
 * The catalog comes from `GET /v2/inventory`, which is paginated server-side and
 * ordered by `id ASC` (newest products last), so the selector keeps the whole
 * catalog in memory and filters it locally: a recently created product is always
 * reachable without typing its id.
 */

export interface ProductOptionLike {
  id: number;
  name: string;
  line?: string | null;
}

/** Trims, lowercases and removes accents so "Línea" matches "linea". */
export function normalizeProductTerm(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

/** Label shown in the field and in every option: "Tile A (Línea A)". */
export function formatProductOptionLabel(product: ProductOptionLike): string {
  return product.line ? `${product.name} (${product.line})` : product.name;
}

/**
 * Filters products by name or line. An empty term returns the very same array
 * (no copy), which keeps trivial renders cheap.
 */
export function filterProductOptions<T extends ProductOptionLike>(
  products: T[],
  term: string
): T[] {
  const normalizedTerm = normalizeProductTerm(term);
  if (!normalizedTerm) return products;

  return products.filter((product) => {
    const matchesName = normalizeProductTerm(product.name).includes(
      normalizedTerm
    );
    const matchesLine = normalizeProductTerm(product.line ?? "").includes(
      normalizedTerm
    );
    return matchesName || matchesLine;
  });
}
